(() => {
  class BatelaPreferenceStore {
    constructor(config) {
      this.name = config.name;
      this.days = config.days;
    }

    read() {
      const prefix = `${this.name}=`;
      const cookie = document.cookie.split('; ').find((item) => item.startsWith(prefix));
      if (!cookie) return null;

      try {
        return JSON.parse(decodeURIComponent(cookie.slice(prefix.length)));
      } catch {
        return null;
      }
    }

    write(value) {
      const maxAge = Math.round(this.days * 86400);
      const secure = location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${this.name}=${encodeURIComponent(JSON.stringify(value))}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
    }
  }

  class BatelaScrollLock {
    constructor() {
      this.scrollY = 0;
      this.touchY = 0;
      this.onWheel = this.onWheel.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
    }

    lock() {
      this.scrollY = window.scrollY;
      document.documentElement.classList.add('batela-scroll-locked');
      document.body.classList.add('batela-scroll-locked');
      document.body.style.top = `-${this.scrollY}px`;
      document.addEventListener('wheel', this.onWheel, { passive: false });
      document.addEventListener('touchstart', this.onTouchStart, { passive: true });
      document.addEventListener('touchmove', this.onTouchMove, { passive: false });
    }

    unlock() {
      document.removeEventListener('wheel', this.onWheel);
      document.removeEventListener('touchstart', this.onTouchStart);
      document.removeEventListener('touchmove', this.onTouchMove);
      document.documentElement.classList.remove('batela-scroll-locked');
      document.body.classList.remove('batela-scroll-locked');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY);
    }

    onWheel(event) {
      if (!this.canListScroll(event.target, event.deltaY)) event.preventDefault();
    }

    onTouchStart(event) {
      this.touchY = event.touches[0]?.clientY || 0;
    }

    onTouchMove(event) {
      const currentY = event.touches[0]?.clientY || this.touchY;
      const deltaY = this.touchY - currentY;
      this.touchY = currentY;
      if (!this.canListScroll(event.target, deltaY)) event.preventDefault();
    }

    canListScroll(target, deltaY) {
      const panel = target.closest?.('.batela-listbox__panel');
      if (!panel) return false;
      if (deltaY < 0 && panel.scrollTop <= 0) return false;
      if (deltaY > 0 && panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1) return false;
      return panel.scrollHeight > panel.clientHeight;
    }
  }

  class BatelaGeoLocation {
    constructor(config) {
      this.flags = config.flags;
    }

    isPreviewContext(params) {
      try {
        const themeRole = window.Shopify?.theme?.role;
        const unpublishedTheme = Boolean(themeRole && themeRole !== 'main');
        const hasPreviewBar = Boolean(document.querySelector('#PreviewBarFrame, .preview-bar-container, shopify-preview-bar'));
        if (params.has('preview_theme_id') || window.Shopify?.designMode || unpublishedTheme || hasPreviewBar) {
          sessionStorage.setItem('batela_geo_preview', '1');
          return true;
        }
        return sessionStorage.getItem('batela_geo_preview') === '1' || ['localhost', '127.0.0.1'].includes(location.hostname);
      } catch {
        return false;
      }
    }

    async getDetectedCountry() {
      const params = new URLSearchParams(location.search);
      const debugCountry = params.get('debug_country')?.trim().toUpperCase();
      if (this.flags.enableGeoDebug && debugCountry) {
        return { country: debugCountry, source: 'debug', force: true };
      }
      if (this.flags.enableGeoDebug && params.has('debug_geo')) {
        const fallback = String(window.Shopify?.country || '').toUpperCase();
        return fallback ? { country: fallback, source: 'debug', force: true } : null;
      }

      if (!this.flags.enableAutomaticGeolocation) return null;

      if (typeof window.BatelaGeoProvider?.getDetectedCountry === 'function') {
        const country = await window.BatelaGeoProvider.getDetectedCountry();
        return country ? { country: String(country).toUpperCase(), source: 'provider' } : null;
      }

      const country = await this.detectShopifyCountry();
      return country ? { country, source: 'shopify' } : null;
    }

    async detectShopifyCountry() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const currentCountry = window.Shopify?.country || '';
        const response = await fetch(
          `${root}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${encodeURIComponent(currentCountry)}`
        );
        if (!response.ok) return null;
        const data = await response.json();
        return data?.detected_values?.country?.handle?.toUpperCase() || null;
      } catch {
        return null;
      }
    }
  }

  const sortCountries = (countries, orderText) => {
    const list = Array.isArray(countries) ? countries.slice() : [];
    const order = String(orderText || '')
      .toUpperCase()
      .split(/[^A-Z]+/)
      .filter((code) => code.length >= 2);
    const rank = new Map(order.map((code, index) => [code, index]));

    return list.sort((a, b) => {
      const aEu = a.market === 'other-eu-countries';
      const bEu = b.market === 'other-eu-countries';
      if (aEu !== bEu) return aEu ? 1 : -1;

      const aRank = rank.has(a.code) ? rank.get(a.code) : order.length;
      const bRank = rank.has(b.code) ? rank.get(b.code) : order.length;
      if (aRank !== bRank) return aRank - bRank;

      return String(a.sortName || a.name || a.code).localeCompare(String(b.sortName || b.name || b.code), 'en');
    });
  };

  const register = () => {
    window.Alpine.data('batelaLocalization', (configId) => {
      const config = JSON.parse(document.getElementById(configId).textContent);
      const preferenceStore = new BatelaPreferenceStore(config.preferenceCookie);
      const scrollLock = new BatelaScrollLock();
      const geolocation = new BatelaGeoLocation(config);

      return {
        config,
        countries: sortCountries(config.countries, config.countryOrder),
        languages: config.languages,
        isOpen: false,
        mode: 'manual',
        openListbox: null,
        temporaryCountry: config.current.country,
        temporaryLanguage: config.current.language,
        committedCountry: config.current.country,
        committedLanguage: config.current.language,
        committedMarket: config.current.market,
        activeIndex: { country: 0, language: 0 },
        activeTrigger: null,
        detectedCountry: null,
        isSubmitting: false,
        statusMessage: '',
        initialized: false,

        get availableLanguages() {
          const country = this.countries.find((item) => item.code === this.temporaryCountry);
          if (Array.isArray(country?.languages) && country.languages.length > 0) {
            return country.languages;
          }
          return this.languages;
        },

        get selectedCountryName() {
          const country = this.countries.find((item) => item.code === this.temporaryCountry);
          return country?.name || country?.display || this.temporaryCountry;
        },

        get selectedLanguageName() {
          return this.availableLanguages.find((language) => language.code === this.temporaryLanguage)?.name || this.temporaryLanguage;
        },

        get selectedLanguageLabel() {
          const language = this.availableLanguages.find((item) => item.code === this.temporaryLanguage);
          return language?.display || language?.name || this.temporaryLanguage;
        },

        init() {
          if (this.initialized) return;
          this.initialized = true;
          this.restoreCommittedPreference();
          this.onDocumentClick = (event) => {
            const trigger = event.target.closest('[data-batela-localization-trigger]');
            if (!trigger) return;
            event.preventDefault();
            document.querySelector('homepage-slide-menu')?.closeMenu?.();
            this.openModal(trigger.dataset.mode || 'manual', trigger);
          };
          document.addEventListener('click', this.onDocumentClick);
          this.$nextTick(() => this.runPreparedLocalization());
        },

        destroy() {
          document.removeEventListener('click', this.onDocumentClick);
          if (this.isOpen) scrollLock.unlock();
        },

        restoreCommittedPreference() {
          if (this.config.flags.enablePreferenceRedirect) {
            const preference = preferenceStore.read();
            if (!this.isValidCountry(preference?.country) || !this.isValidLanguage(preference?.language, preference.country)) return;
            this.committedCountry = preference.country;
            this.committedLanguage = preference.language;
            this.committedMarket = preference.market || '';
            this.resetTemporary();
          }
        },

        async runPreparedLocalization() {
          const params = new URLSearchParams(location.search);
          const isDebug = this.config.flags.enableGeoDebug && Boolean(params.get('debug_country') || params.get('debug_geo'));
          const preference = preferenceStore.read();

          if (!isDebug && this.config.flags.enablePreferenceRedirect && preference) {
            this.applyPreferenceRedirect(preference);
            return;
          }

          const detection = await geolocation.getDetectedCountry();
          if (!detection) return;
          if (!isDebug && preference) return;
          if (!this.isValidCountry(detection.country)) return;

          const dismissalKey = `batela_geo_dismissed_${detection.country}`;
          if (!isDebug && sessionStorage.getItem(dismissalKey) === '1') return;
          if (!isDebug && !detection.force && !this.isLocalizationMismatch(detection.country)) return;

          this.detectedCountry = detection.country;
          this.openModal('geolocation', null, detection.country);
        },

        isLocalizationMismatch(countryCode) {
          return countryCode !== this.config.current.country;
        },

        openModal(mode = 'manual', trigger = null, suggestedCountry = null) {
          if (this.isOpen) return;
          this.mode = mode;
          this.activeTrigger = trigger || document.activeElement;
          this.resetTemporary();
          if (suggestedCountry && this.isValidCountry(suggestedCountry)) {
            this.temporaryCountry = suggestedCountry;
            this.ensureValidLanguage();
          }
          this.statusMessage = '';
          this.isOpen = true;
          scrollLock.lock();
          this.$nextTick(() => this.$refs.closeButton.focus({ preventScroll: true }));
        },

        onEscape() {
          if (!this.isOpen) return;
          if (this.openListbox) {
            this.closeListbox();
            return;
          }
          this.closeModal();
        },

        closeModal() {
          if (!this.isOpen) return;
          this.closeListbox(false);
          if (this.mode === 'geolocation') {
            sessionStorage.setItem(`batela_geo_dismissed_${this.detectedCountry || this.temporaryCountry}`, '1');
          }
          this.resetTemporary();
          this.isOpen = false;
          scrollLock.unlock();
          this.$nextTick(() => this.activeTrigger?.focus?.({ preventScroll: true }));
        },

        resetTemporary() {
          this.temporaryCountry = this.committedCountry;
          this.temporaryLanguage = this.committedLanguage;
          this.ensureValidLanguage();
        },

        toggleListbox(type, trigger) {
          if (this.openListbox === type) {
            this.closeListbox();
            return;
          }
          this.openList(type, trigger);
        },

        openList(type, trigger, direction = 0) {
          this.openListbox = type;
          this.activeTrigger = this.activeTrigger || trigger;
          const items = this.itemsFor(type);
          const selectedCode = type === 'country' ? this.temporaryCountry : this.temporaryLanguage;
          const selectedIndex = Math.max(0, items.findIndex((item) => item.code === selectedCode));
          this.activeIndex[type] = direction < 0 ? items.length - 1 : direction > 0 ? 0 : selectedIndex;
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              this.positionListbox(type, trigger);
              this.focusActiveOption(type);
            });
          });
        },

        closeListbox(restoreFocus = true) {
          const type = this.openListbox;
          this.openListbox = null;
          if (restoreFocus && type) {
            this.$nextTick(() => this.triggerFor(type)?.focus({ preventScroll: true }));
          }
        },

        positionListbox(type, trigger) {
          const panel = this.panelFor(type);
          if (!panel || !trigger || this.openListbox !== type) return;

          const viewportGap = 8;
          const fieldGap = 6;
          const rect = trigger.getBoundingClientRect();
          panel.style.visibility = 'hidden';
          panel.style.width = `${rect.width}px`;
          panel.style.maxHeight = 'none';
          panel.style.left = `${Math.max(viewportGap, Math.min(rect.left, window.innerWidth - rect.width - viewportGap))}px`;

          const availableAbove = Math.max(0, rect.top - viewportGap - fieldGap);
          const naturalHeight = panel.scrollHeight;
          const panelHeight = Math.min(naturalHeight, availableAbove);
          panel.style.maxHeight = `${panelHeight}px`;
          panel.style.top = `${rect.top - panelHeight - fieldGap}px`;
          panel.style.visibility = '';
          this.scrollSelectedIntoView(type);
        },

        repositionOpenListbox() {
          if (!this.isOpen || !this.openListbox) return;
          this.$nextTick(() => this.positionListbox(this.openListbox, this.triggerFor(this.openListbox)));
        },

        scrollSelectedIntoView(type) {
          const panel = this.panelFor(type);
          const selected = panel?.querySelector('[aria-selected="true"]');
          if (!panel || !selected) return;
          const target = selected.offsetTop - panel.clientHeight + selected.offsetHeight * 2;
          panel.scrollTop = Math.max(0, Math.min(target, panel.scrollHeight - panel.clientHeight));
        },

        onTriggerKeydown(event, type, trigger) {
          const key = event.key;
          if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) {
            event.preventDefault();
          }
          if (key === 'ArrowDown') this.openList(type, trigger, 1);
          if (key === 'ArrowUp') this.openList(type, trigger, -1);
          if (key === 'Home') this.openList(type, trigger, 1);
          if (key === 'End') this.openList(type, trigger, -1);
          if (key === 'Enter' || key === ' ') this.toggleListbox(type, trigger);
        },

        onListboxKeydown(event, type) {
          const items = this.itemsFor(type);
          if (!items.length) return;

          if (event.key === 'Tab') {
            event.preventDefault();
            this.closeListbox();
            return;
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.closeListbox();
            return;
          }
          if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
            event.preventDefault();
          }
          if (event.key === 'ArrowDown') this.moveActive(type, 1);
          if (event.key === 'ArrowUp') this.moveActive(type, -1);
          if (event.key === 'Home') this.setActive(type, 0);
          if (event.key === 'End') this.setActive(type, items.length - 1);
          if (event.key === 'Enter' || event.key === ' ') {
            const item = items[this.activeIndex[type]];
            if (type === 'country') this.selectCountry(item.code);
            if (type === 'language') this.selectLanguage(item.code);
          }
        },

        moveActive(type, step) {
          const itemCount = this.itemsFor(type).length;
          this.setActive(type, (this.activeIndex[type] + step + itemCount) % itemCount);
        },

        setActive(type, index) {
          this.activeIndex[type] = index;
          this.$nextTick(() => this.focusActiveOption(type));
        },

        focusActiveOption(type) {
          const option = document.getElementById(this.activeOptionId(type));
          option?.focus({ preventScroll: true });
          const panel = this.panelFor(type);
          if (!option || !panel) return;
          if (option.offsetTop < panel.scrollTop) panel.scrollTop = option.offsetTop;
          if (option.offsetTop + option.offsetHeight > panel.scrollTop + panel.clientHeight) {
            panel.scrollTop = option.offsetTop + option.offsetHeight - panel.clientHeight;
          }
        },

        selectCountry(countryCode) {
          if (!this.isValidCountry(countryCode)) return;
          this.temporaryCountry = countryCode;
          this.ensureValidLanguage();
          this.closeListbox();
        },

        selectLanguage(languageCode) {
          if (!this.isValidLanguage(languageCode, this.temporaryCountry)) return;
          this.temporaryLanguage = languageCode;
          this.closeListbox();
        },

        ensureValidLanguage() {
          if (this.isValidLanguage(this.temporaryLanguage, this.temporaryCountry)) return;
          const country = this.countries.find((item) => item.code === this.temporaryCountry);
          const primary = country?.languages?.find((language) => language.primary);
          this.temporaryLanguage = primary?.code || this.availableLanguages[0]?.code || this.config.current.language;
        },

        isValidCountry(countryCode) {
          return this.countries.some((country) => country.code === countryCode);
        },

        isValidLanguage(languageCode, countryCode = this.temporaryCountry) {
          if (!languageCode) return false;
          const country = this.countries.find((item) => item.code === countryCode);
          const list = country?.languages?.length ? country.languages : this.languages;
          return list.some((language) => language.code === languageCode);
        },

        saveSelection() {
          if (this.isSubmitting) return;
          if (!this.isValidCountry(this.temporaryCountry) || !this.isValidLanguage(this.temporaryLanguage, this.temporaryCountry)) {
            this.statusMessage = this.config.messages?.invalidSelection || 'Invalid selection';
            return;
          }

          const market = this.countries.find((item) => item.code === this.temporaryCountry)?.market
            || (this.temporaryCountry === this.config.current.country ? this.config.current.market : '');
          const preference = {
            country: this.temporaryCountry,
            language: this.temporaryLanguage,
            market
          };
          preferenceStore.write(preference);
          this.committedCountry = preference.country;
          this.committedLanguage = preference.language;
          this.committedMarket = preference.market;
          window.dispatchEvent(new CustomEvent('batela:localization-saved', { detail: preference }));

          if (!this.config.flags.enableMarketSwitching) {
            this.statusMessage = this.config.messages?.testSaved || '';
            window.setTimeout(() => this.closeModal(), 350);
            return;
          }

          if (
            this.temporaryCountry === this.config.current.country
            && this.temporaryLanguage === this.config.current.language
          ) {
            this.closeModal();
            return;
          }

          this.submitLocalization();
        },

        applyPreferenceRedirect(preference) {
          if (!this.config.flags.enableMarketSwitching) return;
          if (!this.isValidCountry(preference.country) || !this.isValidLanguage(preference.language, preference.country)) return;
          const loopKey = `${preference.country}:${preference.language}`;
          if (sessionStorage.getItem('batela_preference_redirect') === loopKey) return;
          if (preference.country === this.config.current.country && preference.language === this.config.current.language) return;
          sessionStorage.setItem('batela_preference_redirect', loopKey);
          this.temporaryCountry = preference.country;
          this.temporaryLanguage = preference.language;
          this.$nextTick(() => this.submitLocalization());
        },

        submitLocalization() {
          if (this.isSubmitting) return;
          this.isSubmitting = true;
          const form = document.getElementById('BatelaLocalizationForm');
          if (!form) {
            this.isSubmitting = false;
            return;
          }
          const countryInput = form.querySelector('[name="country_code"]');
          const languageInput = form.querySelector('[name="locale_code"]');
          if (countryInput) countryInput.value = this.temporaryCountry;
          if (languageInput) languageInput.value = this.temporaryLanguage;
          sessionStorage.setItem(
            'batela_preference_redirect',
            `${this.temporaryCountry}:${this.temporaryLanguage}`
          );
          HTMLFormElement.prototype.submit.call(form);
        },

        trapFocus(event) {
          if (this.openListbox) return;
          const dialog = document.getElementById('BatelaLocalizationModal');
          const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), [href], input:not([type="hidden"]):not([disabled])'));
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        },

        itemsFor(type) {
          return type === 'country' ? this.countries : this.availableLanguages;
        },

        panelFor(type) {
          return document.getElementById(type === 'country' ? 'BatelaCountryListbox' : 'BatelaLanguageListbox');
        },

        triggerFor(type) {
          return type === 'country' ? this.$refs.countryTrigger : this.$refs.languageTrigger;
        },

        optionId(type, index) {
          return `Batela${type === 'country' ? 'Country' : 'Language'}Option-${index}`;
        },

        activeOptionId(type) {
          return this.optionId(type, this.activeIndex[type]);
        }
      };
    });
  };

  if (window.Alpine) {
    register();
  } else {
    document.addEventListener('alpine:init', register, { once: true });
  }
})();
